/* Common Imports */

import React from "react";
import { styled } from "@mui/system";

/* MUI Imports */

import { Slider } from "@mui/material";

/* Helper Imports */

import { brokeragePayout, brokerageUnit, brokerageValueLabel, isRentBrokerage, roundBrokerageValue } from "./brokerage-config";

/* Styled Components */

const Container = styled("div")(() => ({
	display: "flex",
	flexDirection: "column",
	width: "100%",
}));

const ValueRow = styled("div")(() => ({
	display: "flex",
	alignItems: "baseline",
	gap: "0.5rem",
	whiteSpace: "nowrap",
}));

const EndLabels = styled("div")(() => ({
	display: "flex",
	justifyContent: "space-between",
	fontSize: "0.75rem",
	lineHeight: "1.125rem",
	color: "#666666",
}));

/* Component: the one brokerage control for every website requirement flow, the app's rules (decided 1 Nov 2026) */

/* A rental is days of rent (15 to 60, marked at 15, 30 and 60); anything else a percentage (0.5 to 10) */

const BrokerageSlider = ({
	transactionType,
	value,
	budgetMax,
	onChange,
	accentColor = "#FC8019",
	valueColor = "#B05911",
}: {
	transactionType: string;
	value: number;
	budgetMax: number;
	onChange: (value: number) => void;
	accentColor?: string;
	valueColor?: string;
}) => {
	const unit = brokerageUnit(transactionType);
	const isRent = isRentBrokerage(transactionType);
	const current = roundBrokerageValue(value || unit.defaultValue, transactionType);
	const payout = brokeragePayout(budgetMax, current, transactionType);

	return (
		<Container>
			<ValueRow>
				<span style={{ fontSize: "1.25rem", lineHeight: "1.875rem", fontWeight: 500, color: valueColor }}>
					{brokerageValueLabel(current, transactionType)}
				</span>
				{payout > 0 ? (
					<span style={{ fontSize: "0.75rem", lineHeight: "1.125rem", color: "#666666" }}>
						approx. ₹{payout.toLocaleString("en-IN")}
					</span>
				) : null}
			</ValueRow>

			<Slider
				value={current}
				min={unit.min}
				max={unit.max}
				step={unit.step}
				marks={isRent ? unit.marks.map((mark) => ({ value: mark, label: brokerageValueLabel(mark, transactionType) })) : false}
				onChange={(_, next) => onChange(roundBrokerageValue(next as number, transactionType))}
				valueLabelDisplay="off"
				getAriaValueText={(v) => brokerageValueLabel(v, transactionType)}
				aria-label={isRent ? "Brokerage in days of rent" : "Brokerage as a percentage of property value"}
				sx={{
					color: accentColor,
					height: 4,
					/* Rent marks carry their labels under the track; keep the first and last inside the slider */
					marginBottom: isRent ? "1rem" : 0,
					"& .MuiSlider-thumb": {
						width: 20,
						height: 20,
						backgroundColor: "#FFFFFF",
						border: `2px solid ${accentColor}`,
						"&:hover, &.Mui-focusVisible, &.Mui-active": { boxShadow: `0px 0px 0px 6px ${accentColor}29` },
					},
					"& .MuiSlider-rail": { backgroundColor: "#E0E0E0", opacity: 1 },
					"& .MuiSlider-mark": { width: 4, height: 4, borderRadius: "50%", backgroundColor: "#B1A692" },
					"& .MuiSlider-markActive": { backgroundColor: "#FFFFFF" },
					"& .MuiSlider-markLabel": { fontSize: "0.75rem", lineHeight: "1.125rem", color: "#666666", top: 28 },
					"& .MuiSlider-markLabel[data-index='0']": { transform: "translateX(0%)" },
					[`& .MuiSlider-markLabel[data-index='${unit.marks.length - 1}']`]: { transform: "translateX(-100%)" },
				}}
			/>

			{isRent ? null : (
				<EndLabels>
					<span>{brokerageValueLabel(unit.min, transactionType)}</span>
					<span>{brokerageValueLabel(unit.max, transactionType)}</span>
				</EndLabels>
			)}
		</Container>
	);
};

export default BrokerageSlider;
